import {
  ChangeEvent,
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { cn } from "~/lib/utils";

interface VerificationCodeInputProps {
  value?: string;
  length?: number;
  disabled?: boolean;
  focusOnMount?: boolean;
  onChange?: (value: string) => void;
  onComplete?: (value: string) => void;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  containerProps?: React.HTMLAttributes<HTMLDivElement>;
}

export default function VerificationCodeInput({
  value,
  length = 6,
  disabled = false,
  focusOnMount = false,
  onChange,
  onComplete,
  inputProps = {},
  containerProps = {},
}: VerificationCodeInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const [localValue, setLocalValue] = useState("");
  const [isActive, setActive] = useState(false);

  const currentValue = useMemo(() => {
    return value ?? localValue;
  }, [value, localValue]);

  useEffect(() => {
    if (focusOnMount && inputRef.current) {
      inputRef.current.focus();
    }
  }, [focusOnMount]);

  useEffect(() => {
    if (inputProps.disabled) {
      setActive(false);
    }
  }, [inputProps.disabled]);

  const handleClick = () => {
    inputRef.current?.focus();
  };

  const handleInputFocus = () => {
    setActive(true);
  };

  const handleInputBlur = () => {
    setActive(false);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newInputVal = event.target.value.replace(/\s/g, "");
    if (RegExp(`^[0-9]{0,${length}}$`).test(newInputVal)) {
      onChange?.(newInputVal);

      setLocalValue(newInputVal);

      if (newInputVal.length === length) {
        onComplete?.(newInputVal);
        inputRef.current?.blur();
      }
    }
  };

  const handleInputSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    e.target.setSelectionRange(val.length, val.length);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (
      ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)
    ) {
      event.preventDefault();
    }
  };

  const isCharacterInactive = (i: number) => currentValue.length < i;
  const isCharacterFilled = (i: number) => currentValue.length > i;
  const isCharacterSelected = (i: number) => {
    return (
      (currentValue.length === i ||
        (currentValue.length === i + 1 && length === i + 1)) &&
      isActive
    );
  };

  return (
    <div
      data-testid="container"
      className={cn("relative flex justify-center w-full h-[3.75rem] gap-2.5", {
        "opacity-50": disabled,
      })}
      {...containerProps}
    >
      <input
        ref={inputRef}
        id="verificationCode"
        data-testid="verificationCode"
        name="verificationCode"
        aria-label="verification code input"
        inputMode="numeric"
        className="verification-code-input absolute inset-0 box-border bg-transparent text-transparent caret-transparent outline-none border-none"
        value={currentValue}
        disabled={disabled}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onSelect={handleInputSelect}
        {...inputProps}
      />
      {[...Array(length)].map((_, i) => (
        <div
          id={`field-${i}`}
          key={`character-${i}`}
          data-testid={`character-${i}`}
          role="textbox"
          tabIndex={i + 1}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          className={cn(
            "flex flex-1 w-full min-w-[1.875rem] max-w-[2.875rem] rounded-full items-center justify-center text-lg leading-14 text-black border-2 cursor-default select-none box-border",
            {
              "border-darkEmerald":
                isCharacterSelected(i) ||
                (!isCharacterSelected(i) && isCharacterFilled(i)),
              "border-lightGrey":
                !isCharacterSelected(i) && !isCharacterFilled(i),
              "bg-lightGrey": disabled,
              "bg-white": !disabled,
              "outline-solid outline-2 outline-cornflowerblue text-cornflowerblue":
                isCharacterInactive(i),
            },
          )}
        >
          {/* Accessor is not user input */}
          {/* eslint-disable-next-line security/detect-object-injection */}
          {currentValue[i] || ""}
        </div>
      ))}
    </div>
  );
}
